// Catalog Shell - Action Registry
// ============================================================================

import { ActionDefinition, ActionContext, ActionLocation } from './types';

export class ActionRegistry {
    private actions: Map<string, ActionDefinition> = new Map();

    register(action: ActionDefinition): void {
        if (this.actions.has(action.actionId)) {
            console.warn(`ActionRegistry: Overwriting existing action '${action.actionId}'`);
        }
        this.actions.set(action.actionId, action);
    }

    unregister(actionId: string): boolean {
        return this.actions.delete(actionId);
    }

    get(actionId: string): ActionDefinition | null {
        return this.actions.get(actionId) ?? null;
    }

    getAll(): ActionDefinition[] {
        return Array.from(this.actions.values());
    }

    // Get actions available for a given context and location
    getAvailable(context: ActionContext, location?: ActionLocation): ActionDefinition[] {
        return this.getAll().filter(action => {
            // Filter by location if specified
            if (location && !action.locations.includes(location)) {
                return false;
            }

            // Check required capabilities against focused pane's capabilities
            if (!this.checkCapabilities(action, context.capabilities)) {
                return false;
            }

            // Workspace scoping: a module-wide verb that only makes sense in
            // one workspace. Omitted = every workspace; an empty array = none.
            // Skipped when the context carries no shell to ask.
            if (action.workspaces && context.shell) {
                if (!action.workspaces.includes(context.shell.getActiveWorkspace())) {
                    return false;
                }
            }

            // Check context predicate
            if (action.contextPredicate && !action.contextPredicate(context)) {
                return false;
            }

            return true;
        });
    }

    // Get actions for the toolbar (primary actions)
    getToolbarActions(context: ActionContext): ActionDefinition[] {
        return this.getAvailable(context, 'toolbar');
    }

    // Get actions for the overflow menu
    getMenuActions(context: ActionContext): ActionDefinition[] {
        return this.getAvailable(context, 'menu');
    }

    // Get actions for context menu
    getContextMenuActions(context: ActionContext): ActionDefinition[] {
        return this.getAvailable(context, 'contextmenu');
    }

    // Get actions for command palette
    getPaletteActions(context: ActionContext): ActionDefinition[] {
        return this.getAvailable(context, 'palette');
    }

    // Execute an action
    async execute(actionId: string, context: ActionContext): Promise<void> {
        const action = this.get(actionId);
        if (!action) {
            throw new Error(`Unknown action: ${actionId}`);
        }

        if (!this.checkCapabilities(action, context.capabilities)) {
            throw new Error(`Action '${actionId}' requires capabilities not present in focused pane`);
        }

        if (action.contextPredicate && !action.contextPredicate(context)) {
            throw new Error(`Action '${actionId}' context predicate failed`);
        }

        await action.execute(context);
    }

    // Check if the focused pane's capabilities satisfy an action's requirements
    private checkCapabilities(action: ActionDefinition, capabilities: string[]): boolean {
        if (action.requiredCapabilities.length === 0) {
            return true;
        }
        return action.requiredCapabilities.every(cap => capabilities.includes(cap));
    }

    // Check if an action is enabled (not just available)
    isEnabled(actionId: string, context: ActionContext): boolean {
        const action = this.get(actionId);
        if (!action) {
            return false;
        }

        if (action.isEnabled) {
            return action.isEnabled(context);
        }

        return true;
    }
}

// Built-in actions that shells typically need
export function createBuiltinActions(): ActionDefinition[] {
    return [
        {
            actionId: 'shell.close-pane',
            label: 'Close Pane',
            icon: 'close',
            locations: ['contextmenu', 'palette'],
            requiredCapabilities: [],
            execute: (ctx) => {
                if (ctx.focusedPane && ctx.focusedSurface) {
                    ctx.focusedSurface.closePane(ctx.focusedPane.instanceId);
                }
            },
        },
        {
            actionId: 'shell.export-config',
            label: 'Export Configuration...',
            icon: 'download',
            locations: ['menu', 'palette'],
            requiredCapabilities: [],
            execute: (ctx) => {
                const exported = ctx.shell.exportConfiguration();
                const blob = new Blob([JSON.stringify(exported, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `shell-config-${Date.now()}.json`;
                a.click();
                URL.revokeObjectURL(url);
            },
        },
        {
            actionId: 'shell.import-config',
            label: 'Import Configuration...',
            icon: 'upload',
            locations: ['menu', 'palette'],
            requiredCapabilities: [],
            execute: async (ctx) => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.json';
                input.onchange = async () => {
                    const file = input.files?.[0];
                    if (!file) return;
                    const text = await file.text();
                    try {
                        const config = JSON.parse(text);
                        const result = ctx.shell.importConfiguration(config);
                        if (!result.success) {
                            ctx.shell.toast({ message: `Import failed: ${result.error}`, variant: 'error' });
                        }
                    } catch (e) {
                        ctx.shell.toast({ message: `Invalid JSON file: ${e}`, variant: 'error' });
                    }
                };
                input.click();
            },
        },
        {
            actionId: 'shell.clear-config',
            label: 'Clear Configuration',
            icon: 'trash',
            locations: ['menu'],
            requiredCapabilities: [],
            execute: async (ctx) => {
                const ok = await ctx.shell.confirm({
                    title: 'Clear configuration',
                    message: 'Clear all saved configuration and reload? This resets the layout to defaults.',
                    confirmLabel: 'Clear and reload',
                    confirmVariant: 'no',
                    requireExplicitChoice: true,
                });
                if (!ok) return;
                localStorage.clear();
                location.reload();
            },
        },
    ];
}
